import React from 'react';
import { uniqueId } from 'lodash';
import { FieldArrayRenderProps, Field, FieldProps, useFormikContext } from 'formik';
import { FormGroupLabel, Dropdown, TextField, Button, Box } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContentContext } from '@qbe/dfc-content';

import CurrencyTextField from 'components/CurrencyTextField/CurrencyTextField';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import { incomeTypeList, IncomeFormFields, IncomeData } from 'contexts/FormProvider';
import { incomeValidationSchema } from '../schema';
import { useContentComplaintPartners } from 'utils/content';

interface Props {
  arrayHelpers: FieldArrayRenderProps;
  index: number;
}

const IncomeEdit = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const { arrayHelpers, index } = props;

  const { values } = useFormikContext<IncomeFormFields>();

  const { getTextContent } = useContentComplaintPartners({
    context: useContentContext(),
  });

  const onAddIncomeDetails = (idx: number) => {
    const newDetails: IncomeData = {
      id: uniqueId(),
      incomeType: '',
      incomeDescription: '',
      incomeAmount: '',
    };

    arrayHelpers.insert(idx + 1, newDetails);
  };

  const isDisabled = !incomeValidationSchema.isValidSync(values);
  const hasIncome = values.incomeStatus === 'yes';
  const isFull = values.incomeData.length === 5;
  const hasOneElement = values.incomeData.length === 1;

  if (!hasIncome) return null;

  return (
    <>
      <FormSpacer type="pagesection" />
      <div ref={ref} />
      <FormCard variant="sectionTitle" title={`${getTextContent('Income.details.incomeHeading')} - (${index + 1})`}>
        <FormGroupLabel alignCenter title={getTextContent('Income.details.incomeType')} required />
        <FormSpacer type="input" />
        <Field name={`incomeData[${index}].incomeType`}>
          {({ field, meta }: FieldProps) => (
            <Dropdown
              {...field}
              id={field.name}
              label={getTextContent('Income.details.incomeType.label')}
              inputProps={
                {
                  'data-track': field.name,
                  'data-track-value': 'capture',
                } as any
              }
              optionList={incomeTypeList.map((i) => ({ label: i, value: i }))}
              error={meta.touched && !!meta.error}
            />
          )}
        </Field>

        <FormGroupLabel alignCenter title={getTextContent('Income.details.incomeDescription')} required />
        <FormSpacer type="input" />
        <Field name={`incomeData[${index}].incomeDescription`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              multiline
              label={getTextContent('Income.details.incomeDescription.label')}
              maxLength={225}
              data-track="incomeDescription"
              data-track-value="mask"
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormGroupLabel alignCenter title={getTextContent('Income.details.incomeAmount')} required />
        <FormSpacer type="input" />
        <Field name={`incomeData[${index}].incomeAmount`}>
          {({ field, meta }: FieldProps) => (
            <CurrencyTextField
              {...field}
              id={field.name}
              label={getTextContent('Income.details.incomeAmount.label')}
              data-track={getTextContent('Income.details.incomeAmount.label')}
              data-track-value="mask"
              maxLength={10}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="formgroup" />

        <Box display="flex" flexDirection="row" justifyContent="center">
          <Button
            type="button"
            label={getTextContent('Income.details.addIncome.label')}
            variant="secondary-on-light"
            disabled={isFull || isDisabled}
            onClick={() => onAddIncomeDetails(index)}
          />
          <FormSpacer type="input" direction="row" />
          <Button
            type="button"
            label={getTextContent('Income.details.removeIncome.label')}
            data-track={getTextContent('Income.details.removeIncome.label')}
            variant="secondary-on-light"
            disabled={hasOneElement}
            onClick={() => arrayHelpers.remove(index)}
          />
        </Box>
      </FormCard>
    </>
  );
});

IncomeEdit.displayName = 'IncomeEdit';

export default IncomeEdit;
